/**
 * 서버 설치 패키지 — 화면이 쓰는 순수 함수와 업로드.
 *
 * 이름 해석은 **미리보기용**이다. 받을지 말지는 서버가 같은 규칙으로 다시 정한다
 * (`apps/server/src/routes/server-packages.ts` 의 `parsePackageName`). 여기서 먼저 읽는 이유는
 * 수백 MB 를 고른 사람이 올리기 전에 "이 파일은 무엇으로 등록되는가" 를 보게 하려는 것이다.
 */
import type { ServerPackage } from './api/client.js';

const SERVER_NAME =
  /^signageone-server-([0-9A-Za-z][0-9A-Za-z._-]*?)-(docker|native)-(x64|arm64)(-sentinel-agent|-sentinel-server|-grid)?\.zip$/;
const ADDON_NAME = /^signageone-addon-sentinel-(agent|server)-([0-9A-Za-z][0-9A-Za-z._-]*)-(x64|arm64)\.zip$/;

/** 서버 패키지 이름 끝의 용도. `-grid` 는 2026-11-02 전의 이름(지금의 `-sentinel-agent`)이다. */
const SERVER_SUFFIX: Record<string, ServerPackage['edition']> = {
  '': 'standard',
  '-sentinel-agent': 'grid',
  '-grid': 'grid',
  '-sentinel-server': 'grid-server',
};

export interface PackageName {
  version: string;
  variant: ServerPackage['variant'];
  arch: ServerPackage['arch'];
  edition: ServerPackage['edition'];
}

/**
 * 빌더가 붙인 이름을 읽는다 — 서버 패키지 `signageone-server-<판>-<docker|native>-<x64|arm64>[-sentinel-agent|-sentinel-server].zip`
 * (옛 이름 `-grid` 도), 적용 묶음 `signageone-addon-sentinel-<agent|server>-<판>-<x64|arm64>.zip`.
 */
export function parsePackageName(filename: string): PackageName | null {
  const server = SERVER_NAME.exec(filename);
  if (server) {
    const [, version, variant, arch, suffix = ''] = server;
    return {
      version: version as string,
      variant: variant as PackageName['variant'],
      arch: arch as PackageName['arch'],
      edition: SERVER_SUFFIX[suffix] ?? 'standard',
    };
  }
  const addon = ADDON_NAME.exec(filename);
  if (addon) {
    const [, what, version, arch] = addon;
    return {
      version: version as string,
      variant: 'addon',
      arch: arch as PackageName['arch'],
      edition: what === 'server' ? 'grid-server' : 'grid',
    };
  }
  return null;
}

/** 용도 — 서버(`purposeLabel`)와 같은 말. 목록·미리보기에 이것을 앞에 내건다. */
export function purposeLabel(variant: ServerPackage['variant'], edition: ServerPackage['edition']): string {
  if (variant === 'addon') {
    return edition === 'grid-server' ? '설치된 서버에 Sentinel 서버·에이전트 더하기' : '설치된 서버에 Sentinel 에이전트 더하기';
  }
  if (edition === 'grid') return '사이니지 서버 + Sentinel 에이전트';
  if (edition === 'grid-server') return '사이니지 서버 + Sentinel 서버·에이전트';
  return '사이니지 서버';
}

export const VARIANT_LABEL: Record<ServerPackage['variant'], string> = {
  docker: 'Docker 판',
  native: '네이티브 판',
  addon: 'Docker·네이티브 공통',
};

export const ARCH_LABEL: Record<ServerPackage['arch'], string> = {
  x64: 'x86_64 (Intel · AMD)',
  arm64: 'ARM64',
};

export interface PackageGroup {
  version: string;
  items: ServerPackage[];
  /** 이 판에서 내려받을 수 있는 것이 하나라도 있는가. */
  ready: boolean;
}

/**
 * 판별로 묶는다. **서버가 준 순서를 그대로 쓴다**(판은 새것부터, 같은 판에서는 Docker 판 먼저) —
 * 판 이름은 날짜·커밋이 섞인 문자열이라 화면이 따로 정렬하면 서버와 어긋나기 쉽다.
 */
export function groupByVersion(items: readonly ServerPackage[]): PackageGroup[] {
  const groups: PackageGroup[] = [];
  for (const item of items) {
    let group = groups.find((entry) => entry.version === item.version);
    if (!group) {
      group = { version: item.version, items: [], ready: false };
      groups.push(group);
    }
    group.items.push(item);
    if (item.status === 'ready') group.ready = true;
  }
  return groups;
}

/** 인터넷 주소로 접속한 요청이 지나는 중간 프록시(Cloudflare)의 본문 한도. */
export const PUBLIC_BODY_LIMIT = 100 * 1024 * 1024;

/**
 * 사내망 밖의 주소로 콘솔을 열었는가 — 큰 파일을 고르면 **올리기 전에** 경고하려고 본다.
 *
 * 정확할 필요는 없다(막지 않고 말만 한다). 사설 IP·localhost·`.local` 이 아니면 인터넷 주소로 본다.
 */
export function looksPublicHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  if (host === 'localhost' || host.endsWith('.local') || host.endsWith('.localhost')) return false;
  if (host.includes(':')) return !(host === '::1' || /^(fe80|fc|fd)/.test(host));
  const v4 = /^(\d{1,3})\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/.exec(host);
  if (v4) {
    const a = Number(v4[1]);
    const b = Number(v4[2]);
    if (a === 10 || a === 127) return false;
    if (a === 192 && b === 168) return false;
    if (a === 172 && b >= 16 && b <= 31) return false;
    if (a === 100 && b >= 64 && b <= 127) return false;
    return true;
  }
  return true;
}

/** 저장소 PUT 이 실패했을 때 사람이 읽을 말. */
export function putFailureText(status: number): string {
  if (status === 413) {
    return (
      '파일이 너무 큽니다 — 인터넷 주소로 접속하면 100MB 가 넘는 파일은 중간 프록시에서 막힙니다. ' +
      '사내 주소로 콘솔을 열어 다시 올리거나, 서버에서 올리기 스크립트를 쓰세요'
    );
  }
  if (status === 403) return '올리기 주소가 만료되었거나 서명이 맞지 않습니다. 창을 닫고 다시 올려 주세요';
  if (status === 0) return '저장소에 연결하지 못했습니다(네트워크가 끊겼거나 프록시가 요청을 끊었습니다)';
  return `저장소가 파일을 받지 않았습니다 (${status})`;
}

/**
 * 서명 주소로 파일을 **저장소에 직접** 올린다.
 *
 * `fetch` 는 올리는 진행률을 주지 않는다. 수백 MB 를 막대 없이 기다리게 하면 사람은 멈춘 줄
 * 알고 창을 닫는다 — 그래서 XHR 이다. 서버(API)를 거치지 않으므로 서버의 메모리 한도와 무관하다.
 */
export function putWithProgress(
  url: string,
  file: Blob,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('content-type', 'application/zip');
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(putFailureText(xhr.status)));
    };
    xhr.onerror = () => reject(new Error(putFailureText(0)));
    xhr.onabort = () => reject(new DOMException('올리기를 취소했습니다', 'AbortError'));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

/**
 * 받은 주소로 내려받기를 시작한다.
 *
 * 파일을 메모리(blob)로 받지 않는다 — 수백 MB 를 탭에 올리면 브라우저가 느려지고, 받는 동안
 * 진행률도 보이지 않는다. 서명 주소가 `Content-Disposition: attachment` 를 싣고 오므로 링크를
 * 누르기만 하면 브라우저의 다운로드 관리자가 받는다.
 */
export function startDownload(url: string, filename: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}
